import type WhiteboardController from '../controllers/WhiteboardController';
import { isMindMapLeaf, isShape } from '../objects/guards';
import type {
  CreateRecord,
  WriteCapableWhiteboardIntegration,
} from '../shared-types';
import { type CreatableRecordType } from '../types';
import terminology from '../utils/terminology';
import Tool, { type IToolActionInput, type IToolAvailableInput } from './Tool';
import { capitalize } from '../utils/string';

// Shapes that can't be converted to records (generally any shape that doesn't
// support text).
const RECORD_EXCLUDED_SHAPES = [
  'video',
  'image',
  'timeline',
  'icon',
  'integrationRecord',
  'spriteIcon',
  'smartRecord',
];

export const getApplicableSelectedObjects = (selection: fabric.Object) => {
  return selection
    .getObjects()
    .filter(obj => isShape(obj) || isMindMapLeaf(obj))
    .filter(
      obj =>
        !obj.recordId &&
        !RECORD_EXCLUDED_SHAPES.includes(obj.shapeName) &&
        !obj.integrationRecord
    );
};

const selectionToRecordData = (selection: fabric.Object): CreateRecord[] =>
  getApplicableSelectedObjects(selection)
    .map(obj => ({
      id: obj.id,
      text: obj.plainText(),
    }))
    .filter(({ text }) => text && text.length > 0);

export abstract class ToolCreateRecords extends Tool {
  static description: (isPlural: boolean) => string;

  static get category() {
    return '';
  }

  static performAction: (
    controller: WhiteboardController,
    data: CreateRecord[]
  ) => void;

  static get toolName() {
    return this.description(true);
  }

  static action({ controller, selection }: IToolActionInput) {
    const data = selectionToRecordData(selection);

    if (data.length > 0) {
      this.performAction(controller, data);
    } else {
      controller.actionShowMessage(
        'At least one of the selected elements must contain text'
      );
    }
  }
}

abstract class ToolCreateAhaRecords extends ToolCreateRecords {
  static recordType: CreatableRecordType;

  static get category(): string {
    return 'Aha! record';
  }

  static performAction(controller: WhiteboardController, data: CreateRecord[]) {
    controller.actionCreateRecords(this.recordType, data);
  }

  static description(isPlural: boolean) {
    return terminology(capitalize(this.recordType), {
      isPlural,
    });
  }
}

export class ToolCreateFeatures extends ToolCreateAhaRecords {
  static available({ controller }: IToolAvailableInput) {
    return Boolean(controller.recordManager?.canCreateRecordType('features'));
  }

  static recordType = 'features' as const;
}

export class ToolCreateRequirements extends ToolCreateAhaRecords {
  static available({ controller }: IToolAvailableInput) {
    return Boolean(
      controller.recordManager?.canCreateRecordType('requirements')
    );
  }

  static recordType = 'requirements' as const;
}

export class ToolCreateEpics extends ToolCreateAhaRecords {
  static available({ controller }: IToolAvailableInput) {
    return Boolean(controller.recordManager?.canCreateRecordType('epics'));
  }

  static recordType = 'epics' as const;
}

export class ToolCreateGoals extends ToolCreateAhaRecords {
  static available({ controller }: IToolAvailableInput) {
    return Boolean(controller.recordManager?.canCreateRecordType('goals'));
  }

  static recordType = 'goals' as const;
}

export class ToolCreateInitiatives extends ToolCreateAhaRecords {
  static available({ controller }: IToolAvailableInput) {
    return Boolean(
      controller.recordManager?.canCreateRecordType('initiatives')
    );
  }

  static recordType = 'initiatives' as const;
}

export class ToolCreateIdeas extends ToolCreateAhaRecords {
  static available({ controller }: IToolAvailableInput) {
    return Boolean(controller.recordManager?.canCreateRecordType('ideas'));
  }

  static recordType = 'ideas' as const;
}

export class ToolCreatePdlcSteps extends ToolCreateAhaRecords {
  static available({ controller }: IToolAvailableInput) {
    return Boolean(controller.recordManager?.canCreateRecordType('pdlcSteps'));
  }

  static recordType = 'pdlcSteps' as const;
}

export class ToolCreateReleases extends ToolCreateAhaRecords {
  static available({ controller }: IToolAvailableInput) {
    return Boolean(controller.recordManager?.canCreateRecordType('releases'));
  }

  static recordType = 'releases' as const;
}

export const makeCreateIntegrationRecordTool = (
  integration: WriteCapableWhiteboardIntegration<any>
): typeof ToolCreateRecords => {
  return class extends ToolCreateRecords {
    static get category() {
      return integration.title;
    }

    static description(isPlural: boolean) {
      return integration.getRecordTitle(isPlural);
    }

    static performAction(
      controller: WhiteboardController,
      data: CreateRecord[]
    ) {
      void controller.integrationsController.actionStartRecordCreation(
        integration.service,
        data
      );
    }

    static available({ controller }: IToolAvailableInput) {
      return !!(
        window.currentAccount?.whiteboardsAdvanced &&
        controller.recordManager?.canCreateRecordType('integrationRecords')
      );
    }
  };
};
